/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { CluEntitiesCommandBar } from 'src/clu/appShell/cluBuildSchemaPage/components/CluEntitiesCommandbar';
import { CluEntitiesTable } from 'src/clu/appShell/cluBuildSchemaPage/components/CluEntitiesTable';
import { CluEntityCreationModal, CreateCluEntityDto } from 'src/clu/appShell/cluBuildSchemaPage/components/CluEntityCreationModal';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logButtonClick, logButtonSuccessfulClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylist } from 'src/core/themes/luisStylist';
import { EntityCapability } from 'src/core/types/EntityStore';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

export type CluEntityDto = { entityName: string; labelsCount: number; intents: string[]; capabilities: EntityCapability[] };

const messages = defineMessages({
    deleteEntitiesTitle: {
        id: 'clu.cluEntitiesList.deleteEntitiesTitle',
        defaultMessage: 'Delete selected entity(s)?'
    },
    deleteEntitiesBody: {
        id: 'clu.cluEntitiesList.deleteEntitiesBody',
        defaultMessage: 'Are you sure you want to delete the selected entity(s)?'
    },
    emptyStateMessage: {
        id: 'clu.cluEntitiesList.emptyStateMessage',
        defaultMessage: 'No entities created yet.'
    },
    createNewEntityLink: {
        id: 'clu.cluEntitiesList.createNewEntityLink',
        defaultMessage: 'Create a new entity.'
    },
    entities: {
        id: 'clu.cluEntitiesList.entities',
        defaultMessage: 'Entities'
    }
});

const { styleComponent } = getStylist('CluEntitiesList');
const CluEntitiesListContainer = styleComponent<IStackProps>(Stack)('CluEntitiesListContainer', { overflow: 'hidden' });

const defaultModalsState = { isCreateModalHidden: true, isDeleteModalHidden: true };

export const CluEntitiesList = observer(() => {
    const { localize } = useLocalization();
    const history = useHistory();
    const { serviceName } = getPortalFromUrl();
    const [query, setQuery] = React.useState('');
    const { rootStore, dispatcher } = useGlobalState();
    const { selection, selectedItems } = useSelection<CluEntityDto>();
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);
    const [sortKey, setSortKey] = React.useState<SortKeyType<CluEntityDto>>({ key: 'entityName', order: 'asc' });
    const [listItems, setListItems] = React.useState<CluEntityDto[]>([]);
    const [entitiesTracker, setEntitiesTracker] = React.useState(rootStore.currentCluProjectStore.entityStoreHouse.tracker);
    const [isInEmptyState, setIsInEmptyState] = React.useState(false);

    const tracker = useProgressTracker({
        onComplete: () => {
            selection.setAllSelected(false);
            setModalsState({ isDeleteModalHidden: true });
        }
    });

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const currentProject = rootStore.currentCluProjectStore;
    const routes = routeTable[currentProject.projectType === 'Conversation' ? 'clu' : 'orchestration'];

    useEffect(() => {
        const { items: entities, tracker: storedEntitiesTracker } = currentProject.entityStoreHouse;
        const { labelsPerEntity, intentsPerEntity } = currentProject;
        const entitiesColumns = entities.map<CluEntityDto>(e => ({
            entityName: e.name,
            labelsCount: labelsPerEntity.get(e.name),
            intents: intentsPerEntity.get(e.name),
            capabilities: e.capabilities(labelsPerEntity.get(e.name))
        }));

        const sortedEntitiesColumns = [...entitiesColumns].sort((a, b) => {
            const aValue = a[sortKey.key];
            const bValue = b[sortKey.key];
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortKey.order === 'asc' ? aValue - bValue : bValue - aValue;
            }
            return sortKey.order === 'asc' ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
        });

        setIsInEmptyState(storedEntitiesTracker.isCompleted && !entitiesColumns.length);
        setEntitiesTracker(storedEntitiesTracker);

        let filteredEntities = [...sortedEntitiesColumns];
        if (query) {
            filteredEntities = filteredEntities.filter(i => i.entityName.indexOf(query) > -1);
        }

        setListItems(filteredEntities);
    }, [sortKey, currentProject.entityStoreHouse.items.length, query]);

    const onSortColumn = (column: IColumn) => {
        setSortKey({ key: column.key as keyof CluEntityDto, order: column.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    const handleDeleteEntities = () => {
        dispatcher.dispatch('deleteCluEntities', { entityNames: selectedItems.map(entity => entity.entityName), tracker, toastData: {} });
        logButtonSuccessfulClick(serviceName, 'Schema', 'DeleteEntity', 'Entities', currentProject.id);
    };

    const emptyStateMessage = (
        <>
            {localize(messages.emptyStateMessage)}&nbsp;
            <RBACTooltip
                hidden={canWrite}
                displayedUserRole={displayedUserRole}
                data-automation-id="createNewEntityLinkLanguageToolTip"
                componentSurroundedByToolTip={
                    <Link disabled={!canWrite} onClick={() => setModalsState({ isCreateModalHidden: false })}>
                        {localize(messages.createNewEntityLink)}
                    </Link>
                }
            />
        </>
    );

    const handleAddEntity = () => {
        setModalsState({ isCreateModalHidden: false });
        logButtonClick(serviceName, 'Schema', 'AddEntity', 'Entities', currentProject.id);
    };

    const handleEditEntity = () => {
        logButtonClick(serviceName, 'Schema', 'EditEntity', 'Entities', currentProject.id);
        history.push(routes.entityDetailsPage(currentProject.name, selectedItems[0].entityName));
    };

    const handleDeleteEntity = () => {
        setModalsState({ isDeleteModalHidden: false });
        logButtonClick(serviceName, 'Schema', 'DeleteEntity', 'Entities', currentProject.id);
    };

    const handleSubmitAddEntity = (data: CreateCluEntityDto) => {
        logButtonSuccessfulClick(serviceName, 'Schema', 'AddEntity', 'Entities', currentProject.id);
        history.push(routes.entityDetailsPage(currentProject.name, data.name));
    };

    return (
        <CluEntitiesListContainer verticalFill grow>
            <CluEntitiesCommandBar
                query={query}
                onAdd={handleAddEntity}
                onQueryChange={setQuery}
                onEdit={handleEditEntity}
                selectedItems={selectedItems}
                onDelete={handleDeleteEntity}
            />

            {isInEmptyState ? (
                <EmptyStateView message={emptyStateMessage} />
            ) : (
                <CluEntitiesTable
                    items={listItems}
                    sortKey={sortKey}
                    selection={selection}
                    isLoading={entitiesTracker.isRunning}
                    areItemsFiltered={Boolean(query)}
                    overallItemsCount={listItems.length}
                    onSortColumn={onSortColumn}
                    ariaLabel={localize(messages.entities)}
                />
            )}

            <CluEntityCreationModal
                onSubmit={handleSubmitAddEntity}
                hidden={modalsState.isCreateModalHidden}
                onDismiss={() => setModalsState({ isCreateModalHidden: true })}
            />

            <DeletePromptModal
                disabled={tracker.isRunning}
                onDelete={handleDeleteEntities}
                hidden={modalsState.isDeleteModalHidden}
                onDismiss={() => setModalsState({ isDeleteModalHidden: true })}
                dialogContentProps={{ title: localize(messages.deleteEntitiesTitle) }}>
                {localize(messages.deleteEntitiesBody)}
            </DeletePromptModal>
        </CluEntitiesListContainer>
    );
});
